import {
  OnboardingImportOverviewSchema,
  OnboardingImportProgressSchema,
  OnboardingImportReportSchema,
  type OnboardingImportOverview,
  type OnboardingImportProgress,
  type OnboardingImportReport,
} from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";

const BASE_PATH = "/admin/settings/onboarding-import";

/** What one poll of an import says, reduced to what the card shows. */
export type OnboardingImportRunState =
  | { phase: "running"; progress: OnboardingImportProgress | null }
  | { phase: "done"; report: OnboardingImportReport }
  | { phase: "failed"; message: string };

export async function fetchOnboardingImportOverview(): Promise<OnboardingImportOverview> {
  const { data } = await authFetch<unknown>(BASE_PATH);
  return OnboardingImportOverviewSchema.parse(data);
}

export async function startOnboardingImport(tenantIds: string[]): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<unknown>(BASE_PATH, {
    method: "POST",
    body: JSON.stringify({ tenantIds }),
  });
  if (!correlationId) throw new Error("The import did not start");
  return { correlationId };
}

export async function fetchOnboardingImportRun(correlationId: string): Promise<OnboardingImportRunState> {
  const { data } = await authFetch<Record<string, unknown>>(`${BASE_PATH}/status/${correlationId}`);
  return toRunState(data);
}

function toRunState(status: Record<string, unknown>): OnboardingImportRunState {
  if (status.status === "FAILED") {
    return { phase: "failed", message: typeof status.message === "string" ? status.message : "" };
  }
  const report = OnboardingImportReportSchema.safeParse(status.report);
  if (status.status === "COMPLETED" && report.success) return { phase: "done", report: report.data };
  if (status.status === "COMPLETED") return { phase: "failed", message: "" };
  const progress = OnboardingImportProgressSchema.safeParse(status.progress);
  return { phase: "running", progress: progress.success ? progress.data : null };
}
